import { Link } from "react-router-dom"

import { Button } from "./button"
import EquiposEnUso from "../business/EquiposEnUso"
import LogoTimido from "../business/LogoTimido"
import { cn } from "../../lib/utils"

/**
 * Footer del sitio público. Lo usan Home, DemoPage y Plans.
 *
 * Los datos vienen de src/data/navegacion.js, que es la fuente única: antes
 * cada una de las tres páginas declaraba su propio objeto con los mismos SVG
 * copiados y con links a rutas que no existen.
 *
 * El CTA es opcional y va acá adentro, no en cada página: puesto en las páginas
 * volvería a ser el mismo bloque copiado tres veces, que es exactamente lo que
 * este componente vino a resolver.
 *
 * El CTA es una tarjeta del color de la marca; el resto del footer queda claro.
 * Va así y no con el footer entero naranja porque el naranja pega por contraste:
 * teñido todo, el cierre deja de ser el cierre y las columnas de links —que son
 * larguísimas, con los rubros— quedan a puro blanco translúcido.
 *
 * `mostrarEquipos` agrega la notebook y el teléfono al costado. Es opt-in por
 * página a propósito: en /admin/plans el footer lo ve alguien que ya es
 * cliente, y no tiene sentido venderle la app con una maqueta. Hoy sólo lo
 * prende Home.
 */
export function Footer({
  logo,
  brandName,
  descripcion,
  cta,
  mostrarEquipos = false,
  socialLinks = [],
  columnas = [],
  rubros = [],
  copyright,
  className,
}) {
  const conEquipos = mostrarEquipos && Boolean(cta);

  return (
    <footer className={cn("border-t border-neutral-200 bg-neutral-50", className)}>
      {/* `overflow-hidden` en la sección: el logo tímido sale por los costados
          de la tarjeta, y en una pantalla donde la tarjeta casi toca los bordes
          eso se pasaría del viewport y le metería scroll horizontal a toda la
          página. El componente además se limita solo midiendo el espacio que
          hay; esto es la red por si alguna vez no alcanza. */}
      {cta && (
        <div id={cta.id} className="overflow-hidden px-6 py-14 lg:px-8 lg:py-20">
          {/* Este envoltorio existe para el logo tímido: `inset-0` sobre él le
              da exactamente el tamaño de la tarjeta, que es contra lo que se
              miden los bordes por los que asoma. El `max-w-7xl mx-auto` se mudó
              de la tarjeta acá para que las dos midan lo mismo. */}
          <div className="relative mx-auto max-w-7xl">
          <div
            className={cn(
              // `relative z-10`: la tarjeta va por ENCIMA del logo, y al ser
              // opaca es la que lo esconde. Sin esto el logo se vería entero
              // flotando por delante del naranja.
              "relative z-10 overflow-hidden rounded-3xl bg-primary px-6 py-14 text-primary-foreground shadow-xl sm:px-10 lg:py-16",
              conEquipos && "lg:px-14"
            )}
          >
            <div
              className={cn(
                "flex flex-col items-center gap-12 text-center",
                // Con los equipos al lado el texto se alinea a la izquierda: un
                // texto centrado contra una ilustración a la derecha deja el
                // bloque descolgado del eje.
                conEquipos && "lg:flex-row lg:items-center lg:gap-16 lg:text-left"
              )}
            >
              <div className={cn("flex flex-col items-center", conEquipos ? "lg:items-start" : "max-w-3xl")}>
                <h2 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]">
                  {cta.titulo}
                </h2>
                {cta.bajada && (
                  <p className="mt-4 max-w-xl text-pretty text-base leading-relaxed text-primary-foreground/85 sm:text-lg">
                    {cta.bajada}
                  </p>
                )}
                <div
                  className={cn(
                    "mt-8 flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center",
                    conEquipos && "lg:justify-start"
                  )}
                >
                  <Button asChild size="cta" variant="inverso">
                    <Link to={cta.accion.href}>{cta.accion.label}</Link>
                  </Button>
                  {cta.secundaria && (
                    <Button asChild size="cta" variant="inversoOutline">
                      <Link to={cta.secundaria.href}>{cta.secundaria.label}</Link>
                    </Button>
                  )}
                </div>
                {cta.nota && (
                  <p className="mt-6 text-sm text-primary-foreground/75">{cta.nota}</p>
                )}
              </div>

              {conEquipos && (
                <div className="flex w-full shrink-0 justify-center lg:w-auto lg:flex-1">
                  <EquiposEnUso />
                </div>
              )}
            </div>
          </div>

          {/* El logo asomándose por detrás de la tarjeta. Va acá afuera y no
              adentro porque la tarjeta tiene `overflow-hidden` para su
              redondeo: adentro quedaría recortado y no podría sobresalir.
              Sólo con el CTA: sin la tarjeta arriba no hay dónde esconderse. */}
          <LogoTimido />
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-6 py-14 lg:px-8 lg:py-16">
        <div className="grid grid-cols-2 gap-x-8 gap-y-12 lg:grid-cols-12">
          {/* Marca */}
          <div className="col-span-2 lg:col-span-4">
            <a href="/" className="inline-flex items-center gap-x-2" aria-label={brandName}>
              {logo}
              <span className="text-xl font-bold text-neutral-900">{brandName}</span>
            </a>
            {descripcion && (
              <p className="mt-4 max-w-xs text-sm leading-relaxed text-neutral-600">
                {descripcion}
              </p>
            )}
            {socialLinks.length > 0 && (
              <ul className="mt-6 flex list-none gap-3">
                {socialLinks.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label={link.label}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-600 transition-colors hover:border-neutral-300 hover:text-neutral-900"
                    >
                      {link.icon}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Columnas de links */}
          {columnas.map((col) => (
            <nav key={col.titulo} className="lg:col-span-2">
              <p className="text-sm font-semibold text-neutral-900">{col.titulo}</p>
              <ul className="mt-4 list-none space-y-3">
                {col.links.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      className="text-sm text-neutral-600 underline-offset-4 transition-colors hover:text-neutral-900 hover:underline"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          ))}

          {/* Rubros. Texto, no links: no existen páginas por rubro. */}
          {rubros.length > 0 && (
            <div className="col-span-2 lg:col-span-2">
              <p className="text-sm font-semibold text-neutral-900">Para tu rubro</p>
              <ul className="mt-4 list-none space-y-3">
                {rubros.map((nombre) => (
                  <li key={nombre} className="text-sm text-neutral-600">
                    {nombre}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {copyright && (
          <div className="mt-12 flex flex-col gap-2 border-t border-neutral-200 pt-8 text-sm text-neutral-500 sm:flex-row sm:items-center sm:justify-between">
            <p>{copyright.text}</p>
            {copyright.license && <p>{copyright.license}</p>}
          </div>
        )}
      </div>
    </footer>
  );
}
